import type { AudioEvent, ShipState } from "../simulation/snapshot";
import type { ComboState, ScoreFeedEntry } from "../simulation/scoring";

/** Trauma tiers — squared falloff keeps spikes readable, tails subtle (Eiserloh). */
const TRAUMA_DECAY_PER_S = 1.85;
const HITSTOP_MAX_S = 0.09;
const HITSTOP_COOLDOWN_S = 0.11;
const FOV_KICK_MAX_DEG = 5.5;
const FOV_KICK_ATTACK = 14;
const FOV_KICK_RELEASE = 5.5;

export type JuiceImpulse = {
  trauma: number;
  hitstopS: number;
  fovKick: number;
  punchX: number;
  punchZ: number;
};

export type CombatJuiceSample = {
  /** 0–1 shake amplitude after trauma². */
  shake: number;
  offsetX: number;
  offsetY: number;
  offsetZ: number;
  fovKickDeg: number;
  hitstopActive: boolean;
  boostLines: number;
};

/**
 * Presentation-only combat feel: trauma shake, micro hitstop, FOV kick.
 * Sim stays authoritative — this only gates/render-flavors the view.
 */
export class CombatJuice {
  private trauma = 0;
  private hitstopLeft = 0;
  private hitstopCd = 0;
  private fovKick = 0;
  private boostLines = 0;
  private punchX = 0;
  private punchZ = 0;
  private timeS = 0;
  private reducedMotion = false;
  private lastChain = 0;

  public setReducedMotion(on: boolean): void {
    this.reducedMotion = on;
    if (on) {
      this.trauma = 0;
      this.hitstopLeft = 0;
      this.fovKick = 0;
      this.boostLines = 0;
      this.punchX = 0;
      this.punchZ = 0;
    }
  }

  /** True while freeze-frames should skip sim steps. */
  public isFrozen(): boolean {
    return !this.reducedMotion && this.hitstopLeft > 0;
  }

  /** Advance freeze clock; returns true if still frozen (caller should skip sim). */
  public tickHitstop(dt: number): boolean {
    if (this.hitstopCd > 0) this.hitstopCd = Math.max(0, this.hitstopCd - dt);
    if (this.hitstopLeft <= 0) return false;
    this.hitstopLeft = Math.max(0, this.hitstopLeft - dt);
    return this.hitstopLeft > 0;
  }

  public ingest(
    events: readonly AudioEvent[],
    player: ShipState,
    combo: ComboState,
    /** Only freshly pushed feed rows for this tick — not the lingering HUD list. */
    freshFeed: readonly ScoreFeedEntry[] = [],
  ): void {
    if (this.reducedMotion) {
      this.lastChain = combo.chainCount;
      return;
    }

    for (const event of events) {
      switch (event.type) {
        case "weapon_fire":
          this.applyImpulse({
            trauma: 0.08,
            hitstopS: 0,
            fovKick: 0.05,
            punchX: -Math.cos(player.facing) * 0.12,
            punchZ: -Math.sin(player.facing) * 0.12,
          });
          break;
        case "boost":
          this.applyImpulse({
            trauma: 0.12,
            hitstopS: 0.018,
            fovKick: 0.85,
            punchX: 0,
            punchZ: 0.2,
          });
          this.boostLines = Math.min(1, this.boostLines + 0.7);
          break;
        case "enemy_death": {
          const elite = event.elite ? 1.35 : 1;
          const chainBoost = Math.min(0.35, combo.chainCount * 0.03);
          this.applyImpulse({
            trauma: (0.38 + chainBoost) * elite,
            hitstopS: event.elite ? 0.07 : 0.045,
            fovKick: 0.15,
            punchX: dirFrom(player, event).x * 0.55,
            punchZ: dirFrom(player, event).z * 0.55,
          });
          break;
        }
        case "hull_hit":
        case "shield_hit": {
          if (event.x === undefined || event.y === undefined) break;
          const dx = event.x - player.x;
          const dy = event.y - player.y;
          const dist = Math.hypot(dx, dy);
          if (dist > 3.5) {
            // Outbound hit confirm (enemy took damage).
            this.applyImpulse({
              trauma: event.type === "shield_hit" ? 0.1 : 0.16,
              hitstopS: event.type === "shield_hit" ? 0.012 : 0.028,
              fovKick: 0.04,
              punchX: (dx / Math.max(dist, 0.01)) * 0.28,
              punchZ: (dy / Math.max(dist, 0.01)) * 0.28,
            });
          } else {
            // Inbound player damage — readable punch, no freeze (keeps dodge responsive).
            this.applyImpulse({
              trauma: 0.32,
              hitstopS: 0,
              fovKick: -0.12,
              punchX: -dx * 0.04,
              punchZ: -dy * 0.04,
            });
          }
          break;
        }
        case "shield_break":
          this.applyImpulse({
            trauma: 0.5,
            hitstopS: 0.04,
            fovKick: -0.2,
            punchX: 0,
            punchZ: 0,
          });
          break;
        case "death":
          this.applyImpulse({
            trauma: 0.85,
            hitstopS: 0.08,
            fovKick: -0.35,
            punchX: 0,
            punchZ: 0,
          });
          break;
        case "extract_complete":
          this.applyImpulse({
            trauma: 0.28,
            hitstopS: 0.03,
            fovKick: 0.25,
            punchX: 0,
            punchZ: 0.15,
          });
          break;
        default:
          break;
      }
    }

    for (const entry of freshFeed) {
      if (entry.kind === "double_kill" || entry.kind === "triple_kill") {
        this.applyImpulse({
          trauma: entry.kind === "triple_kill" ? 0.42 : 0.28,
          hitstopS: entry.kind === "triple_kill" ? 0.055 : 0.035,
          fovKick: 0.2,
          punchX: 0,
          punchZ: 0,
        });
      }
      if (entry.kind === "chain" && combo.chainCount > 0 && combo.chainCount % 5 === 0) {
        this.applyImpulse({
          trauma: 0.22,
          hitstopS: 0.03,
          fovKick: 0.1,
          punchX: 0,
          punchZ: 0,
        });
      }
    }

    this.lastChain = combo.chainCount;
  }

  /** Call once per render frame with wall-clock dt. */
  public sample(dt: number, boosting: boolean): CombatJuiceSample {
    this.timeS += dt;
    if (this.reducedMotion) {
      return {
        shake: 0,
        offsetX: 0,
        offsetY: 0,
        offsetZ: 0,
        fovKickDeg: 0,
        hitstopActive: false,
        boostLines: 0,
      };
    }

    this.trauma = Math.max(0, this.trauma - TRAUMA_DECAY_PER_S * dt);
    this.punchX *= Math.exp(-10 * dt);
    this.punchZ *= Math.exp(-10 * dt);

    const boostTarget = boosting ? 1 : 0;
    const boostRate = boosting ? FOV_KICK_ATTACK : FOV_KICK_RELEASE;
    this.fovKick += (boostTarget - this.fovKick) * (1 - Math.exp(-boostRate * dt));
    this.boostLines += ((boosting ? 1 : 0) - this.boostLines) * (1 - Math.exp(-(boosting ? 10 : 4) * dt));

    const shake = this.trauma * this.trauma;
    const t = this.timeS * 22;
    // Smooth noise-ish offsets (two phase-offset sins ≈ cheap Perlin substitute).
    const ox = Math.sin(t * 1.7) * Math.cos(t * 0.9) * shake * 1.15 + this.punchX;
    const oy = Math.sin(t * 2.1 + 1.3) * shake * 0.55;
    const oz = Math.cos(t * 1.9) * Math.sin(t * 1.1) * shake * 1.15 + this.punchZ;

    return {
      shake,
      offsetX: ox,
      offsetY: oy,
      offsetZ: oz,
      fovKickDeg: this.fovKick * FOV_KICK_MAX_DEG,
      hitstopActive: this.hitstopLeft > 0,
      boostLines: this.boostLines,
    };
  }

  public getLastChain(): number {
    return this.lastChain;
  }

  private applyImpulse(impulse: JuiceImpulse): void {
    this.trauma = Math.min(1, this.trauma + impulse.trauma);
    this.fovKick = Math.max(-0.4, Math.min(1.2, this.fovKick + impulse.fovKick * 0.35));
    this.punchX += impulse.punchX;
    this.punchZ += impulse.punchZ;
    if (impulse.hitstopS > 0 && this.hitstopCd <= 0) {
      this.hitstopLeft = Math.min(HITSTOP_MAX_S, Math.max(this.hitstopLeft, impulse.hitstopS));
      this.hitstopCd = HITSTOP_COOLDOWN_S;
    }
  }
}

function dirFrom(
  player: ShipState,
  event: { x?: number; y?: number },
): { x: number; z: number } {
  if (event.x === undefined || event.y === undefined) return { x: 0, z: 0 };
  const dx = event.x - player.x;
  const dz = event.y - player.y;
  const len = Math.hypot(dx, dz) || 1;
  return { x: dx / len, z: dz / len };
}
